/* Repository path: frontend/src/api/support.ts */

import type { SupportLinkResponse } from "../types";
import { request } from "./client";

/**
 * The client's own copy of the provider allowlist, mirroring
 * `planner.support.config.PROVIDERS`. This is **defence in depth**, not
 * the primary control — the server already resolves and validates the
 * link — but a page must never trust a fetched value for something as
 * consequential as an outbound `href` without checking it again itself.
 * See docs/architecture/features/W4-09.md §4 and
 * docs/architecture/adr/ADR-0013-external-support-link-boundary.md.
 */
const PROVIDER_HOSTS: Readonly<Record<string, readonly string[]>> = {
  ko_fi: ["ko-fi.com", "www.ko-fi.com"],
  buy_me_a_coffee: ["buymeacoffee.com", "www.buymeacoffee.com"],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Re-checks scheme, host and the absence of userinfo, a port, a query
 * string or a fragment — the same properties the server already enforced.
 * A `javascript:` or `data:` URL, or a URL for a host this provider does
 * not own, can never reach an `href` from this module, even if the server
 * response were malformed or compromised.
 */
function isSafeSupportUrl(provider: string, url: string): boolean {
  const hosts = PROVIDER_HOSTS[provider];
  if (!hosts) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") return false;
  if (parsed.username || parsed.password) return false;
  if (parsed.port) return false;
  if (!hosts.includes(parsed.hostname)) return false;
  if (parsed.search || parsed.hash) return false;
  return true;
}

function isValidEnabledPayload(
  value: Record<string, unknown>,
): value is { enabled: true; provider: string; provider_label: string; url: string } {
  return (
    value.enabled === true
    && typeof value.provider === "string"
    && typeof value.provider_label === "string"
    && typeof value.url === "string"
    && isSafeSupportUrl(value.provider, value.url)
  );
}

/**
 * `GET /api/v1/support` (W4-09). **Never rejects and never throws** — a
 * network failure, an offline browser, a `404`, `429`, `503`, malformed
 * JSON, an unknown provider code or a URL that fails the allowlist all
 * resolve to `{ enabled: false }`, the same shape the server itself sends
 * when the feature is off. The prompt this feeds is never on the critical
 * path of anything, and a support component should never need a `catch`
 * of its own. See docs/architecture/features/W4-09.md §4.
 */
export async function fetchSupportLink(signal?: AbortSignal): Promise<SupportLinkResponse> {
  try {
    const value = await request<unknown>("/support", { signal });
    if (!isRecord(value)) return { enabled: false };
    if (isValidEnabledPayload(value)) {
      return {
        enabled: true,
        provider: value.provider,
        provider_label: value.provider_label,
        url: value.url,
      };
    }
    return { enabled: false };
  } catch {
    return { enabled: false };
  }
}
